import React, { useEffect, useState } from 'react';
import { Box, MenuItem, Select, SelectChangeEvent, TextField, Typography } from '@mui/material';
import { FormMessage } from '../../types';
import { getDelayAmount, getDelayPeriod } from '../../helpers/delayHelpers';
import { CONTROL_HEIGHT } from './controlSizing';

const numberFieldSx = (width?: string) => ({
  marginRight: '10px',
  '& .MuiOutlinedInput-input': {
    height: CONTROL_HEIGHT,
    paddingTop: 0,
    paddingBottom: 0,
    ...(width ? { width } : {}),
  },
});

interface UseDelayFieldOptions {
  message: FormMessage;
  onChange: (next: Partial<FormMessage>) => void;
  /** Unit selected before the user picks one. Reminder starts on days. */
  defaultPeriod: string;
  /**
   * Adopt the unit from an already-saved delay on mount.
   *
   * BEHAVIOUR NOTE: the flyer renderer never did this, so a saved `"2d"` shows
   * as "Minutes" there. Preserved as-is — see the behaviour-fix list.
   */
  syncPeriodOnMount: boolean;
}

/** The delay amount/unit pair that four schedule renderers each re-implemented. */
export function useDelayField({
  message,
  onChange,
  defaultPeriod,
  syncPeriodOnMount,
}: UseDelayFieldOptions) {
  const [amount, setAmount] = useState<string>(
    message.scheduledType === 'delay' && message.delay ? getDelayAmount(message.delay) : '',
  );
  const [period, setPeriod] = useState<string>(defaultPeriod);

  useEffect(() => {
    if (syncPeriodOnMount && message.scheduledType === 'delay' && message.delay) {
      setPeriod(getDelayPeriod(message.delay, defaultPeriod));
    }
    // Mount-only: the saved delay seeds the unit once.
  }, []);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setAmount(value);
    onChange({ time: null, delay: `${value}${period}` });
  };

  const handlePeriodChange = ({ target }: SelectChangeEvent<string>) => {
    setPeriod(target.value);
    onChange({ time: null, delay: `${amount}${target.value}` });
  };

  /** Clear both when the user leaves the delay schedule type. */
  const clear = () => {
    setAmount('');
    setPeriod(defaultPeriod);
  };

  return { amount, period, handleAmountChange, handlePeriodChange, clear };
}

export interface DelayInputProps {
  amount: string;
  period: string;
  onAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPeriodChange: (e: SelectChangeEvent<string>) => void;
  disabled?: boolean;
  /** Reminder narrows the number field and appends a trailing caption. */
  inputWidth?: string;
  trailingLabel?: string;
  /** Reminder centres the inner row so its trailing caption lines up. */
  alignItems?: 'center';
  /** Join centres the outer wrapper instead of stacking it. Defaults to column. */
  outerSx?: Record<string, unknown>;
}

/** Number field + Minutes/Hours/Days select — the delay editor every renderer shows. */
export const DelayInput: React.FC<DelayInputProps> = ({
  amount,
  period,
  onAmountChange,
  onPeriodChange,
  disabled,
  inputWidth,
  trailingLabel,
  alignItems,
  outerSx = { flexDirection: 'column', marginTop: '13px' },
}) => (
  <Box display="flex" sx={outerSx}>
    <Box display="flex" {...(alignItems ? { alignItems } : {})}>
      <TextField
        disabled={disabled}
        sx={numberFieldSx(inputWidth)}
        placeholder="Period"
        name="delay"
        type="number"
        inputProps={{ min: 1 }}
        variant="outlined"
        value={amount}
        onChange={onAmountChange}
      />
      <Select
        disabled={disabled}
        // The height is pinned below, but the size still decides the inner
        // padding — without it the unit sits lower than the number beside it.
        size="small"
        sx={{ height: CONTROL_HEIGHT }}
        MenuProps={{ disableScrollLock: true }}
        value={period}
        onChange={onPeriodChange}
        name="delayPeriod"
      >
        <MenuItem value="m">Minutes</MenuItem>
        <MenuItem value="h">Hours</MenuItem>
        <MenuItem value="d">Days</MenuItem>
      </Select>
      {trailingLabel && (
        <Typography component="label" color="text.primary" ml={1.25}>
          {trailingLabel}
        </Typography>
      )}
    </Box>
  </Box>
);
